'use client';

import { PublicPageLayout } from '@letsgo/PublicPageLayout';
import Link from 'next/link';
import { Box, Container, Typography, Link as MuiLink, Paper } from '@mui/material';

const STEP_IDS = ['account-profile', 'connect-channel', 'first-post', 'explore-tools', 'get-help'] as const;

export default function GettingStartedPage() {
  return (
    <PublicPageLayout>
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Typography variant="h4" component="h1" fontWeight={700} mb={1} color="text.primary">
          Getting Started
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={4}>
          New to FloAI? Follow these steps to go from signup to your first post and beyond.
        </Typography>

        <Typography component="h2" id={STEP_IDS[0]} variant="h5" fontWeight={700} mt={4} mb={2} color="text.primary">
          Step 1: Account & profile
        </Typography>
        <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'action.hover', borderRadius: 2 }}>
          <Typography variant="body1" paragraph sx={{ mb: 0 }}>
            Sign up at <MuiLink component={Link} href="/auth/signup" color="primary">Sign up</MuiLink>, verify your email, then set your display name and avatar in your profile. Your profile is where you control how you appear and manage account details.
          </Typography>
          <MuiLink component={Link} href="/dashboard/profile" color="primary" sx={{ fontWeight: 600 }}>Go to Profile (after sign-in) →</MuiLink>
        </Paper>

        <Typography component="h2" id={STEP_IDS[1]} variant="h5" fontWeight={700} mt={4} mb={2} color="text.primary">
          Step 2: Connect a channel
        </Typography>
        <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'action.hover', borderRadius: 2 }}>
          <Typography variant="body1" paragraph sx={{ mb: 0 }}>
            Link at least one social platform so you can create and schedule content. In your dashboard, open <strong>Profile</strong> and use the <strong>Connected Accounts / Integrations</strong> section to connect Facebook, Instagram, Twitter/X, and other supported platforms. Follow the on-screen prompts to authorize.
          </Typography>
          <MuiLink component={Link} href="/dashboard/profile" color="primary" sx={{ fontWeight: 600 }}>Connected Accounts (Profile) →</MuiLink>
        </Paper>

        <Typography component="h2" id={STEP_IDS[2]} variant="h5" fontWeight={700} mt={4} mb={2} color="text.primary">
          Step 3: First post
        </Typography>
        <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'action.hover', borderRadius: 2 }}>
          <Typography variant="body1" paragraph sx={{ mb: 0 }}>
            Create or schedule your first piece of content. Use <strong>Create</strong> to draft a post and pick platforms and time, or use the <strong>Content Calendar</strong> on your dashboard to plan by date. You can also use a template from the calendar templates tool to get started quickly.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            <MuiLink component={Link} href="/dashboard/create" color="primary" sx={{ fontWeight: 600 }}>Create page →</MuiLink>
            <MuiLink component={Link} href="/tools/calendar-templates" color="primary" sx={{ fontWeight: 600 }}>Calendar templates →</MuiLink>
          </Box>
        </Paper>

        <Typography component="h2" id={STEP_IDS[3]} variant="h5" fontWeight={700} mt={4} mb={2} color="text.primary">
          Step 4: Explore tools
        </Typography>
        <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'action.hover', borderRadius: 2 }}>
          <Typography variant="body1" paragraph>
            FloAI includes several tools to level up your workflow:
          </Typography>
          <Box component="ul" sx={{ pl: 2.5, mt: 0 }}>
            <li><MuiLink component={Link} href="/tools/social-media-calculator" color="primary">Social Media Calculator</MuiLink> — Estimate ROI and value of your social presence.</li>
            <li><MuiLink component={Link} href="/tools/hashtag-research" color="primary">Hashtag Research</MuiLink> — Find and compare hashtags for reach and relevance.</li>
            <li><MuiLink component={Link} href="/tools/social-media-audit" color="primary">Social Media Audit</MuiLink> — Review handle performance and get recommendations.</li>
            <li><MuiLink component={Link} href="/tools/ai-content-creation" color="primary">AI Content Creation</MuiLink> — Generate captions and ideas with AI.</li>
            <li><MuiLink component={Link} href="/tools/voice-tone-analyzer" color="primary">Voice & Tone Analyzer</MuiLink> — Check content tone and alignment with your brand.</li>
          </Box>
        </Paper>

        <Typography component="h2" id={STEP_IDS[4]} variant="h5" fontWeight={700} mt={4} mb={2} color="text.primary">
          Step 5: Get help
        </Typography>
        <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'action.hover', borderRadius: 2 }}>
          <Typography variant="body1" paragraph sx={{ mb: 0 }}>
            Need answers or support? Use the <MuiLink component={Link} href="/faq" color="primary">FAQ</MuiLink>, <MuiLink component={Link} href="/support" color="primary">Support</MuiLink> page, <MuiLink component={Link} href="/tutorials" color="primary">Guides & Tutorials</MuiLink>, or our <MuiLink component={Link} href="/community" color="primary">Community</MuiLink> (coming soon). You can also <MuiLink component={Link} href="/contact" color="primary">contact us</MuiLink> directly.
          </Typography>
        </Paper>
      </Container>
    </PublicPageLayout>
  );
}
